import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Send, Search, Eye, FileText, Building2, Calendar, CheckCircle2, XCircle, Clock, AlertCircle, DollarSign, ArrowLeft, RefreshCw } from 'lucide-react';
import { payrollAPI } from '../../../services/api';
import { toast } from 'sonner';

const GLPosting = () => {
  const [postings, setPostings] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [showPostingModal, setShowPostingModal] = useState(false);
  const [selectedRunForPosting, setSelectedRunForPosting] = useState(null);
  const [posting, setPosting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [postingsRes, runsRes] = await Promise.all([payrollAPI.getGLPostings(), payrollAPI.getRuns({ status: 'CHECK_RESULTS' })]);
      setPostings(postingsRes.data || []);
      const completedRunsRes = await payrollAPI.getRuns({ status: 'COMPLETED' });
      const allRuns = [...(runsRes.data || []), ...(completedRunsRes.data || [])];
      const postedRunIds = (postingsRes.data || []).map(p => p.payroll_run_id);
      setRuns(allRuns.filter(r => !postedRunIds.includes(r.id)));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load GL postings');
    } finally { setLoading(false); }
  };

  const handleCreatePosting = async () => {
    if (!selectedRunForPosting) { toast.error('Please select a payroll run'); return; }
    setPosting(true);
    try {
      await payrollAPI.createGLPosting(selectedRunForPosting, 'ADMIN');
      toast.success('GL posting created successfully');
      setShowPostingModal(false);
      setSelectedRunForPosting(null);
      loadData();
    } catch (error) {
      console.error('Failed to create GL posting:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create GL posting');
      }
    } finally { setPosting(false); }
  };

  const getStatusBadge = (status) => ({ DRAFT: 'bg-yellow-100 text-yellow-800', POSTED: 'bg-green-100 text-green-800', REVERSED: 'bg-red-100 text-red-800', ERROR: 'bg-red-100 text-red-800' }[status] || 'bg-gray-100 text-gray-800');
  const getStatusIcon = (status) => { switch (status) { case 'POSTED': return <CheckCircle2 className="w-4 h-4 text-green-600" />; case 'REVERSED': return <XCircle className="w-4 h-4 text-red-600" />; case 'ERROR': return <AlertCircle className="w-4 h-4 text-red-600" />; default: return <Clock className="w-4 h-4 text-yellow-600" />; } };
  const filteredPostings = postings.filter(p => (p.document_number || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.payroll_run_number || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.company_code_id || '').toLowerCase().includes(searchTerm.toLowerCase()));
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  if (selectedPosting) {
    return (
      <Layout>
        <div className="p-6" data-testid="gl-posting-detail">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => setSelectedPosting(null)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">GL Posting: {selectedPosting.document_number}</h1>
              <p className="text-gray-600">Payroll Run: {selectedPosting.payroll_run_number} | Period: {selectedPosting.period_description}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedPosting.status)}`}>{selectedPosting.status}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4"><div className="flex items-center gap-3"><Building2 className="w-8 h-8 text-orange-600" /><div><p className="text-sm text-gray-500">Company Code</p><p className="font-semibold">{selectedPosting.company_code_name || selectedPosting.company_code_id}</p></div></div></Card>
            <Card className="p-4"><div className="flex items-center gap-3"><Calendar className="w-8 h-8 text-green-600" /><div><p className="text-sm text-gray-500">Posting Date</p><p className="font-semibold">{selectedPosting.posting_date}</p></div></div></Card>
            <Card className="p-4"><div className="flex items-center gap-3"><DollarSign className="w-8 h-8 text-emerald-600" /><div><p className="text-sm text-gray-500">Total Debits</p><p className="font-semibold">{formatCurrency(selectedPosting.total_debits)}</p></div></div></Card>
            <Card className="p-4"><div className="flex items-center gap-3"><DollarSign className="w-8 h-8 text-red-600" /><div><p className="text-sm text-gray-500">Total Credits</p><p className="font-semibold">{formatCurrency(selectedPosting.total_credits)}</p></div></div></Card>
          </div>
          <Card className="overflow-hidden">
            <div className="p-4 border-b bg-gray-50"><h2 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" />GL Line Items</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="line-items-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Line</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">GL Account</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Account Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cost Center</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Debit</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Credit</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Text</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(selectedPosting.line_items || []).map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.line_number}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.gl_account}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.gl_account_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.cost_center || '-'}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">{item.assignment_type}</span></td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-700">{item.debit_amount > 0 ? formatCurrency(item.debit_amount) : '-'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-red-700">{item.credit_amount > 0 ? formatCurrency(item.credit_amount) : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.text}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr><td colSpan="5" className="px-4 py-3 text-sm text-right">Totals:</td><td className="px-4 py-3 text-sm text-right text-green-700">{formatCurrency(selectedPosting.total_debits)}</td><td className="px-4 py-3 text-sm text-right text-red-700">{formatCurrency(selectedPosting.total_credits)}</td><td></td></tr>
                </tfoot>
              </table>
            </div>
          </Card>
          <Card className="p-4 mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-500">Fiscal Year</p><p className="font-medium">{selectedPosting.fiscal_year}</p></div>
              <div><p className="text-gray-500">Fiscal Period</p><p className="font-medium">{selectedPosting.fiscal_period}</p></div>
              <div><p className="text-gray-500">Posted By</p><p className="font-medium">{selectedPosting.posted_by || '-'}</p></div>
              <div><p className="text-gray-500">Posted At</p><p className="font-medium">{selectedPosting.posted_at ? new Date(selectedPosting.posted_at).toLocaleString() : '-'}</p></div>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="gl-posting-list">
        <div className="flex justify-between items-center mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">GL Posting</h1><p className="text-gray-600 mt-1">Post payroll results to the General Ledger</p></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
            <Button onClick={() => setShowPostingModal(true)} className="bg-orange-600 hover:bg-orange-700" disabled={runs.length === 0} data-testid="create-posting-btn"><Send className="w-4 h-4 mr-2" />Post to GL</Button>
          </div>
        </div>
        {runs.length > 0 && (<Card className="p-4 mb-6 bg-amber-50 border-amber-200"><div className="flex items-center gap-3"><AlertCircle className="w-5 h-5 text-amber-600" /><div><p className="font-medium text-amber-800">{runs.length} payroll run(s) ready for GL posting</p><p className="text-sm text-amber-600">Click "Post to GL" to create finance entries</p></div></div></Card>)}
        <Card className="p-4 mb-6"><div className="flex items-center gap-2"><Search className="w-4 h-4 text-gray-400" /><Input placeholder="Search by document number, run number, or company..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="flex-1" data-testid="search-input" /></div></Card>
        <Card className="overflow-hidden">
          {loading ? (<div className="p-8 text-center text-gray-500">Loading...</div>) : filteredPostings.length === 0 ? (<div className="p-8 text-center text-gray-500">{searchTerm ? 'No postings found matching your search' : 'No GL postings created yet'}</div>) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="postings-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Document #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payroll Run</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Posting Date</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPostings.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.document_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{p.payroll_run_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{p.company_code_name || p.company_code_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{p.period_description}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{p.posting_date}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(p.total_debits)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(p.status)}`}>{getStatusIcon(p.status)}{p.status}</span></td>
                      <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => setSelectedPosting(p)} data-testid={`view-${p.id}`}><Eye className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {showPostingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg p-6 m-4" data-testid="posting-modal">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Send className="w-5 h-5 text-orange-600" />Post Payroll to GL</h2>
              <p className="text-gray-600 mb-4">Select a payroll run to generate GL posting entries.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Payroll Run</label>
                  <select value={selectedRunForPosting || ''} onChange={(e) => setSelectedRunForPosting(e.target.value)} className="w-full p-2 border rounded-md" data-testid="run-select">
                    <option value="">-- Select a run --</option>
                    {runs.map(run => (<option key={run.id} value={run.id}>{run.run_number} - {run.payroll_area_name} ({run.period_year}/{run.period_number})</option>))}
                  </select>
                </div>
                {runs.length === 0 && (<p className="text-sm text-amber-600">No payroll runs available for posting.</p>)}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowPostingModal(false); setSelectedRunForPosting(null); }} data-testid="cancel-modal-btn">Cancel</Button>
                <Button onClick={handleCreatePosting} disabled={!selectedRunForPosting || posting} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-posting-btn">{posting ? 'Posting...' : 'Create GL Posting'}</Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GLPosting;
